import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { Box, Divider, Icon, Text } from '@chakra-ui/react';
import { EditorView } from '@tiptap/pm/view';
import { Editor, Range } from '@tiptap/react';
import { BiChevronLeft } from 'react-icons/bi';

import { ThirdPartySvg } from '@/components/common';
import { IconButton } from '@/components/core';
import { FONTS_MORE_OPACITY, FONTS_OPACITY } from '@/constant';
import { glassyEffectButton } from '@/styles/variants/glassy';

import {
  RefinedSuggestionItem,
  RefinedSuggestionItemGroup,
} from './SlashExtension.types';

export const CommandList = forwardRef(
  (
    {
      items: itemGroup,
      command,
      query,
    }: {
      items: RefinedSuggestionItemGroup;
      command: (item: { command: RefinedSuggestionItem['command'] }) => void;
      query: string;
    },
    ref: ForwardedRef<{
      onKeyDown: (data: {
        event: KeyboardEvent;
        range: Range;
        view: EditorView;
      }) => void;
    }>
  ) => {
    const { items, groupLabel } = itemGroup;

    const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(
      null
    );

    useEffect(() => {
      setSelectedItemIndex(null);
    }, [items?.length]);

    useImperativeHandle(
      ref,
      () => ({
        onKeyDown: ({ event }) => {
          if (event.key === 'ArrowUp') {
            setSelectedItemIndex(prev =>
              prev === null ? 0 : Math.max(0, prev - 1)
            );
            return true;
          }

          if (event.key === 'ArrowDown') {
            setSelectedItemIndex(prev =>
              prev === null ? 0 : Math.min(items!.length - 1, prev + 1)
            );
            return true;
          }

          if (event.key === 'Enter') {
            if (selectedItemIndex !== null) {
              command(items![selectedItemIndex]);
            }
            return true;
          }
        },
      }),
      [command, items, selectedItemIndex]
    );

    const handleGoBack = useCallback(() => {
      command({
        command: ({ editor, range }: { editor: Editor; range: Range }) => {
          editor
            .chain()
            .focus()
            .deleteRange({ from: range.from + 1, to: range.to })
            .run();
        },
      });
    }, [command]);

    const isChildGroup = itemGroup.groupLabel;

    const baseWindowHeight =
      window.visualViewport?.height || window.innerHeight;
    const maxHeight = `${baseWindowHeight - 100}px`;

    return (
      <Box
        zIndex={10000}
        bg='gray.800'
        color='white'
        border='1px solid rgba(255, 255, 255, 0.15)'
        p={4}
        boxShadow='2xl'
        display='flex'
        flexDirection='column'
        gap={3.5}
        borderRadius='md'
        w='524px'
        maxW='100vw'
        id='slash-extension-command-list'
        maxH={maxHeight}
      >
        {isChildGroup ? (
          <Box display='flex' alignItems='center' gap={2.5}>
            <IconButton
              aria-label='Back'
              icon={<Icon as={BiChevronLeft} boxSize={4} />}
              onClick={handleGoBack}
              variant='glassy'
            />
            {groupLabel}
          </Box>
        ) : (
          <Text fontSize={13} opacity={FONTS_OPACITY}>
            Suggestions
          </Text>
        )}
        <Divider />
        {items?.length === 0 && (
          <Text
            id='no-commands'
            fontStyle='italic'
            opacity={FONTS_MORE_OPACITY}
            textAlign='center'
            my='42px'
          >
            No items found
          </Text>
        )}
        <Box
          display='grid'
          gridTemplateColumns={{
            base: '1fr',
            md: isChildGroup ? '1fr' : 'repeat(2, 1fr)',
          }}
          gap={3}
          overflowY='auto'
        >
          {items?.map((item, index) =>
            isChildGroup ? (
              <SuggestionItem
                key={item.label}
                item={item}
                command={command}
                query={query}
                isSelected={selectedItemIndex === index}
              />
            ) : (
              <SuggestionGroupItem
                key={item.label}
                item={item}
                command={command}
                isSelected={selectedItemIndex === index}
              />
            )
          )}
        </Box>
      </Box>
    );
  }
);

function SuggestionGroupItem({
  item,
  command,
  isSelected,
}: {
  item: RefinedSuggestionItem;
  command: (item: RefinedSuggestionItem) => void;
  isSelected: boolean;
}) {
  return (
    <Box
      display='flex'
      alignItems='center'
      justifyContent='flex-start'
      gap={2.5}
      onClick={() => command(item)}
      cursor='pointer'
      borderRadius='md'
      _hover={{
        color: 'alternative.purple',
      }}
      _active={{
        color: 'alternative.purple',
      }}
      color={isSelected ? 'alternative.purple' : undefined}
    >
      {item.icon && (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          w={8}
          h={8}
          sx={glassyEffectButton}
          aria-label={item.label}
        >
          <ThirdPartySvg src={item.icon} boxSize={4} />
        </Box>
      )}
      {item.label}
    </Box>
  );
}

function SuggestionItem({
  item,
  command,
  query,
  isSelected,
}: {
  item: RefinedSuggestionItem;
  command: (item: RefinedSuggestionItem) => void;
  query: string;
  isSelected: boolean;
}) {
  const matchedQueryLength = useMatchedQueryLength(query, item.label);

  return (
    <Box
      display='flex'
      alignItems='center'
      justifyContent='flex-start'
      gap={2.5}
      onClick={() => command(item)}
      px={3}
      py={1.5}
      borderRadius='md'
      cursor='pointer'
      _hover={{
        bg: 'whiteAlpha.50',
      }}
      bg={isSelected ? 'whiteAlpha.50' : 'transparent'}
    >
      {item.icon && (
        <ThirdPartySvg src={item.icon} boxSize={4} opacity={FONTS_OPACITY} />
      )}
      <Text>
        <Text as='span' color='#BA99F0'>
          {item.label.slice(0, matchedQueryLength)}
        </Text>
        <Text as='span'>{item.label.slice(matchedQueryLength)}</Text>
      </Text>
    </Box>
  );
}

function useMatchedQueryLength(query: string, text: string) {
  return useMemo(() => {
    const lowerQuery = query.toLowerCase().trimStart();
    const lowerText = text.toLowerCase();

    let matchLength = 0;

    while (
      matchLength < lowerText.length &&
      matchLength < lowerQuery.length &&
      lowerText[matchLength] === lowerQuery[matchLength]
    ) {
      matchLength++;
    }

    return matchLength;
  }, [query, text]);
}
